import React, { useCallback } from 'react'

import { useRouter } from 'next/router'
import {
  HiOutlineBriefcase,
  HiOutlineBuildingLibrary,
  HiOutlineChatBubbleLeftRight,
  HiOutlineDocumentText,
  HiOutlineEnvelope,
  HiOutlineGlobeAlt,
  HiOutlineInbox,
  HiSparkles,
} from 'react-icons/hi2'

import {
  ShimmerCtaButton,
  StepDots,
  useIntroWizard,
  WizardContinueButton,
} from '@/components/announcements/IntroWizard'
import type { AnnouncementBodyProps } from '@/components/announcements/types'
import { savePendingChatAutoSend } from '@/components/chat-v2/lib/localStorage'
import { DialogTitle } from '@/components/common/Dialog'
import { ONBOARDING_CHAT_MESSAGE } from '@/components/monitors/constants'

import { cn } from '@/helpers/shadcn/utils'

import { useActiveOrgId } from '@/hooks/organization/useActiveOrgId'

const RADAR_RING_DELAYS = [0, 1, 2]

interface SourceChipProps {
  className: string
  icon: React.ReactNode
  label: string
}

const SourceChip: React.FC<SourceChipProps> = ({ className, icon, label }) => (
  <div
    className={cn(
      'absolute inline-flex items-center gap-1.5 rounded-full border border-primary-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-primary-600 shadow-sm dark:border-primary-800 dark:bg-primary-900 dark:text-primary-300',
      className,
    )}
  >
    {icon}
    {label}
  </div>
)

// Step 1 — a radar sweep with the four things a monitor can watch orbiting it.
const RadarVisual: React.FC = () => (
  <>
    {RADAR_RING_DELAYS.map((delay) => (
      <span
        key={delay}
        style={{ animationDelay: `${delay}s` }}
        className="absolute left-1/2 top-1/2 h-[150px] w-[150px] -translate-x-1/2 -translate-y-1/2 animate-ping rounded-full border-[1.5px] border-accent-500/50 [animation-duration:3s]"
      />
    ))}
    <span className="absolute left-1/2 top-1/2 h-24 w-24 -translate-x-1/2 -translate-y-1/2 rounded-full border border-dashed border-accent-500/25" />
    <span className="absolute left-1/2 top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 animate-pulse rounded-full bg-accent-500" />
    <SourceChip
      className="left-8 top-14"
      icon={<HiOutlineBriefcase className="h-3.5 w-3.5 text-amber-600" />}
      label="Vendor"
    />
    <SourceChip
      className="right-10 top-6"
      icon={
        <HiOutlineBuildingLibrary className="h-3.5 w-3.5 text-purple-600" />
      }
      label="Agency"
    />
    <SourceChip
      className="bottom-6 left-10"
      icon={<HiOutlineDocumentText className="h-3.5 w-3.5 text-blue-600" />}
      label="Opportunity"
    />
    <SourceChip
      className="bottom-6 right-10"
      icon={<HiOutlineGlobeAlt className="h-3.5 w-3.5 text-teal-600" />}
      label="Web"
    />
  </>
)

// Step 2 — a matched alert card landing in the inbox, plus delivery channels.
const InboxVisual: React.FC = () => (
  <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
    <div className="flex w-[340px] overflow-hidden rounded-xl border border-primary-200 bg-white shadow-md dark:border-primary-800 dark:bg-primary-900">
      <div className="w-[3px] shrink-0 bg-accent-500" />
      <div className="min-w-0 flex-1 px-3 py-2.5">
        <div className="flex items-center justify-between gap-2">
          <span className="text-[10px] font-bold tracking-wider text-primary-400 dark:text-primary-500">
            DELTEK
          </span>
          <span className="text-[10px] text-primary-400 dark:text-primary-500">
            1d ago
          </span>
        </div>
        <div className="mt-0.5 truncate text-[12.5px] font-semibold text-primary-900 dark:text-primary-100">
          New award · DoD cybersecurity
        </div>
        <div className="mt-1.5 flex items-center gap-1.5">
          <span className="rounded border border-primary-200 bg-primary-100 px-1.5 py-px text-[9px] font-bold tracking-wide text-primary-400 dark:border-primary-700 dark:bg-primary-800 dark:text-primary-500">
            CONTRACT
          </span>
          <span className="rounded-full bg-accent-500/15 px-1.5 py-px text-[9.5px] font-bold text-accent-600">
            MATCHED
          </span>
        </div>
      </div>
    </div>
    <div className="flex items-center gap-2">
      <span className="inline-flex h-6 items-center gap-1.5 rounded-full bg-accent-500 px-2.5 text-[11px] font-semibold text-white shadow-sm">
        <HiOutlineInbox className="h-3 w-3" />
        Inbox
      </span>
      <span className="inline-flex h-6 items-center gap-1.5 rounded-full border border-primary-200 bg-white px-2.5 text-[11px] font-semibold text-primary-600 shadow-sm dark:border-primary-800 dark:bg-primary-900 dark:text-primary-300">
        <HiOutlineChatBubbleLeftRight className="h-3 w-3" />
        Slack
      </span>
      <span className="inline-flex h-6 items-center gap-1.5 rounded-full border border-primary-200 bg-white px-2.5 text-[11px] font-semibold text-primary-600 shadow-sm dark:border-primary-800 dark:bg-primary-900 dark:text-primary-300">
        <HiOutlineEnvelope className="h-3 w-3" />
        Email
      </span>
    </div>
  </div>
)

// Step 3 — the plain-English prompt that hands off to AI setup.
const AiPromptVisual: React.FC = () => (
  <div className="absolute inset-0 flex items-center justify-center">
    <div className="flex w-[340px] items-center gap-3 rounded-xl border border-violet-500/40 bg-white px-4 py-3 shadow-[0_0_0_4px_rgba(124,58,237,0.08)] dark:bg-primary-900">
      <HiSparkles className="h-[18px] w-[18px] shrink-0 text-violet-600" />
      <span className="flex-1 text-[13px] leading-snug text-primary-900 dark:text-primary-100">
        Watch Leidos and all DoD cybersecurity opportunities
        <span className="ml-0.5 inline-block h-3.5 w-0.5 -translate-y-px animate-pulse bg-violet-600 align-middle" />
      </span>
    </div>
  </div>
)

const STEPS = [
  {
    title: 'Never miss an opportunity again',
    body: 'Monitors keep watch on the vendors, agencies, opportunities, and web sources you care about — and surface new awards, amendments, and updates the moment they happen.',
    Visual: RadarVisual,
  },
  {
    title: 'Delivered where you work',
    body: 'Every match lands in your Monitors inbox, and can be pushed to Slack or email. Star what matters, archive the rest — it’s your feed for everything moving in your market.',
    Visual: InboxVisual,
  },
  {
    title: 'Set it up in seconds with AI',
    body: 'Just tell our AI what you want to track in plain English and it’ll build your monitors for you — no forms, no filters to configure.',
    Visual: AiPromptVisual,
  },
]

/**
 * The Monitors GA onboarding wizard — a 3-step introduction that funnels the
 * user into "Set up my monitors with AI". Rendered inside the shared
 * AnnouncementModal, so the single close button comes from the dialog chrome.
 */
export const MonitorsIntroModal: React.FC<AnnouncementBodyProps> = ({
  onDismiss,
}) => {
  const router = useRouter()
  const { activeOrgId } = useActiveOrgId()
  const { step, isLast, enterClass, handleNext, handleBack } = useIntroWizard(
    STEPS.length,
  )

  const handleSetUpWithAi = useCallback(() => {
    if (activeOrgId) {
      savePendingChatAutoSend({
        message: ONBOARDING_CHAT_MESSAGE,
        orgId: activeOrgId,
        agentName: 'organization_chat',
      })
      void router.push(`/orgs/${activeOrgId}/c/new`)
    }
    onDismiss()
  }, [activeOrgId, router, onDismiss])

  const handleExplore = useCallback(() => {
    if (activeOrgId) {
      void router.push(`/orgs/${activeOrgId}/monitors`)
    }
    onDismiss()
  }, [activeOrgId, router, onDismiss])

  const active = STEPS[step]
  const Visual = active.Visual

  return (
    <div>
      <div className="relative h-[182px] overflow-hidden border-b border-primary-200 bg-gradient-to-b from-accent-400/15 via-violet-500/[0.06] to-transparent dark:border-primary-800">
        <div className="absolute left-5 top-4 z-10 inline-flex items-center gap-1 rounded-full border border-accent-500/30 bg-white/80 px-2.5 py-1 text-[11px] font-semibold text-accent-600 backdrop-blur dark:bg-primary-900/70">
          <HiSparkles className="h-3 w-3" />
          New
        </div>
        <div key={step} className={cn('absolute inset-0', enterClass)}>
          <Visual />
        </div>
      </div>

      <div className="px-6 pb-1 pt-5">
        <div key={step} className={enterClass}>
          <DialogTitle className="text-[21px] font-bold leading-tight tracking-[-0.02em] text-primary-900 dark:text-primary-100">
            {active.title}
          </DialogTitle>
          <p className="mt-2 text-sm leading-relaxed text-primary-600 dark:text-primary-300">
            {active.body}
          </p>
        </div>
      </div>

      {isLast ? (
        <div className="flex flex-col gap-3.5 px-6 pb-6 pt-5">
          <div className="flex items-center justify-between">
            <StepDots count={STEPS.length} step={step} />
            <button
              type="button"
              onClick={handleBack}
              className="text-[12.5px] font-medium text-primary-400 transition hover:text-primary-700 dark:text-primary-500 dark:hover:text-primary-200"
            >
              Back
            </button>
          </div>
          <div className="flex items-stretch gap-2.5">
            <button
              type="button"
              onClick={handleExplore}
              className="inline-flex h-11 flex-1 items-center justify-center gap-2 rounded-[10px] border border-primary-900 bg-primary-900 px-4 text-sm font-semibold text-white transition-transform duration-150 ease-out hover:bg-primary-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-2 active:scale-[0.97] dark:border-primary-200 dark:bg-primary-100 dark:text-primary-900 dark:hover:bg-white dark:focus-visible:ring-offset-default-dark"
            >
              <HiOutlineInbox className="h-4 w-4 shrink-0" />
              Explore on my own
            </button>
            <ShimmerCtaButton
              onClick={handleSetUpWithAi}
              className="h-11 flex-1 border border-violet-700 px-4 dark:border-violet-600"
            >
              <HiSparkles className="h-4 w-4 shrink-0" />
              Set up with AI
            </ShimmerCtaButton>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-between gap-4 px-6 pb-6 pt-5">
          <StepDots count={STEPS.length} step={step} />
          <div className="flex items-center gap-2.5">
            {step > 0 ? (
              <button
                type="button"
                onClick={handleBack}
                className="h-10 rounded-[10px] border border-primary-300 px-4 text-sm font-medium text-primary-600 transition hover:bg-primary-100 dark:border-primary-700 dark:text-primary-300 dark:hover:bg-primary-800"
              >
                Back
              </button>
            ) : null}
            <WizardContinueButton onClick={handleNext} />
          </div>
        </div>
      )}
    </div>
  )
}
